import {
  AppointmentStatusCodes,
  QueryFactory,
  ResourceDto,
} from '@lmt/shared-types';
import {
  CalendarConfigurationDto,
  DictionaryRecordDto,
  SubtenantDto,
  ResourceType,
} from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { MultiSelectDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/MultiSelectDropDownInput';
import { getEnumValuesDict } from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { calendarParams } from '@lmt/unicorn-shared/app/constants/calendarParams';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import {
  useSubtenants,
  useSubtenantsDict,
} from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { VisitType } from '../visit/common/types';

import styles from '@lmt/unicorn-shared/app/components/calendar/filter/EventFilter.module.scss';
import { useTags } from '../../common/ui-kit/tags/useTags';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from '@mui/material';
import { isEmpty } from '../../measurements/copy-datablock-field/common';
import { useSortedResources } from '@lmt/unicorn-shared/app/api/custom-hooks';

interface Props {
  configuration?: CalendarConfigurationDto;
  setConfigurationQueryParams: (
    configuration: CalendarConfigurationDto,
  ) => void;
  needToRemoveFiltersOnConfigurationChange?: boolean;
}

function checkIfFilterOptionsExistOrEmpty(
  selectedOptions: (number | null)[],
  existingOptions: { id: number }[],
) {
  if (isEmpty(selectedOptions)) {
    return true;
  }
  return !!existingOptions.find((x) => selectedOptions.includes(x.id));
}

export const EventFilter: FC<Props> = (props) => {
  const i18n = useScopedTranslation('calendar.filter');
  const {
    configuration,
    needToRemoveFiltersOnConfigurationChange,
    setConfigurationQueryParams,
  } = props;
  if (!configuration) {
    return null;
  }
  const {
    hasFilterBySubtenants,
    hasFilterByDoctors,
    hasFilterByVisitTypes,
    hasFilterByPlaces,
    hasFilterBySpecialConditions,
    hasFilterByVisitStatuses,
    hasFilterByTags,
  } = configuration;
  const i18nextGlobal = useTranslation();
  const subtenants: SubtenantDto[] = useSubtenants();
  const subtenantsDict = useSubtenantsDict();
  const visitTypes = useActiveVisitTypes();
  const translatedVisitTypes: VisitType[] = useMemo(() => {
    if (!hasFilterByVisitTypes || !visitTypes.length) {
      return [];
    }
    return visitTypes.map((type) => ({
      id: type.id,
      label: i18nextGlobal.t(type.value as any),
    }));
  }, [visitTypes, configuration]);

  const translatedVisitStatuses = useMemo(
    () => getEnumValuesDict(AppointmentStatusCodes, 'visit_status'),
    [],
  );

  const allTags = useTags();

  // selected filter options are stored in its own state because filter is applied on button click ('Apply')
  // we need to store selected options before filter is applied
  const [selectedSubtenants, setSelectedSubtenants] = useState<number[]>([]);
  const [selectedDoctors, setSelectedDoctors] = useState<number[]>([]);
  const [selectedVisitTypes, setSelectedVisitTypes] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [selectedVisitStatuses, setSelectedVisitStatuses] = useState<number[]>(
    [],
  );
  const [
    selectedSpecialConditionsResources,
    setSelectedSpecialConditionsResources,
  ] = useState<number[]>([]);
  const [selectedPlacesResources, setSelectedPlacesResources] = useState<
    number[]
  >([]);

  const [parsedQueryString, setQueryString] =
    useQueryParamsInTab(calendarParams);

  useEffect(() => {
    // reset selection for those previously selected items which didn't get included in updated list
    const updatedVisibleTagsIds = allTags.map((item) => item.id);
    setSelectedTags(
      selectedTags.filter((id) => updatedVisibleTagsIds.includes(id)),
    );
  }, [allTags]);

  function selectSubtenants(subtenants: ReadonlyArray<SubtenantDto>) {
    setSelectedSubtenants(subtenants.map((subtenant) => subtenant.id));
  }

  function selectDoctors(doctors: ReadonlyArray<ResourceDto>) {
    setSelectedDoctors(doctors.map((doctor) => doctor.id));
  }

  function selectVisitTypes(types: ReadonlyArray<VisitType>) {
    setSelectedVisitTypes(types.map((type) => type.id));
  }

  function selectVisitStatuses(
    statuses: ReadonlyArray<AppointmentStatusCodes>,
  ) {
    setSelectedVisitStatuses(statuses as any);
  }

  function selectTags(tags: ReadonlyArray<DictionaryRecordDto>) {
    setSelectedTags(tags.map((item) => item.id));
  }

  function selectSpecialConditionsResources(
    resources: ReadonlyArray<ResourceDto>,
  ) {
    setSelectedSpecialConditionsResources(resources.map((item) => item.id));
  }

  function selectPlacesResources(resources: ReadonlyArray<ResourceDto>) {
    setSelectedPlacesResources(resources.map((item) => item.id));
  }

  const resetConfigurationValues = useEventCallback(
    (configuration: CalendarConfigurationDto) => {
      if (!configuration) {
        return;
      }
      setSelectedPlacesResources(configuration.places ?? []);
      setSelectedSpecialConditionsResources(
        configuration.specialConditions ?? [],
      );
      setSelectedDoctors(configuration.doctors ?? []);
      setSelectedSubtenants(configuration.subtenants ?? []);
      setSelectedVisitTypes(configuration.visitTypes ?? []);
      setSelectedTags(configuration.tags ?? []);
      setSelectedVisitStatuses(configuration.visitStatuses as number[]);
    },
  );

  useEffect(function setInitialFilters() {
    setSelectedPlacesResources(parsedQueryString.places as number[]);
    setSelectedSpecialConditionsResources(
      parsedQueryString.specialConditions as number[],
    );
    setSelectedDoctors(parsedQueryString.doctors as number[]);
    setSelectedSubtenants(parsedQueryString.subtenants as number[]);
    setSelectedVisitTypes(parsedQueryString.visitTypes as number[]);
    setSelectedTags(parsedQueryString.tags as number[]);
    setSelectedVisitStatuses(parsedQueryString.visitStatuses as number[]);
  }, []);

  useEffect(
    function resetFilterOnConfigurationChange() {
      if (needToRemoveFiltersOnConfigurationChange) {
        resetConfigurationValues(configuration);
      }
    },
    [configuration],
  );

  useEffect(() => {
    if (!isEmpty(selectedSubtenants)) {
      var updatedSubtenants = selectedSubtenants.filter(
        (n) => !!subtenantsDict[n],
      );
      setQueryString({ subtenants: updatedSubtenants });
    }
  }, [subtenants]);

  const specialConditionResources = useSortedResources(
    ResourceType.SpecialConditions,
  );

  const placeResources = useSortedResources(ResourceType.Places);

  const doctors = useSortedResources(ResourceType.Doctors);

  useEffect(() => {
    const isPlacesExist = checkIfFilterOptionsExistOrEmpty(
      parsedQueryString.places,
      placeResources,
    );
    const isSpecialConditionsExist = checkIfFilterOptionsExistOrEmpty(
      parsedQueryString.specialConditions,
      specialConditionResources,
    );
    const isDoctorsExist = checkIfFilterOptionsExistOrEmpty(
      parsedQueryString.doctors,
      doctors,
    );
    const isTagsExist = checkIfFilterOptionsExistOrEmpty(
      parsedQueryString.tags,
      allTags,
    );
    const isVisitTypesExist = checkIfFilterOptionsExistOrEmpty(
      parsedQueryString.visitTypes,
      visitTypes,
    );
    const isSubtenantsExist = checkIfFilterOptionsExistOrEmpty(
      parsedQueryString.subtenants,
      subtenants,
    );

    if (
      isPlacesExist &&
      isSpecialConditionsExist &&
      isDoctorsExist &&
      isTagsExist &&
      isVisitTypesExist &&
      isSubtenantsExist
    ) {
      return;
    }

    setQueryString({
      places: isPlacesExist ? parsedQueryString.places : [],
      specialConditions: isSpecialConditionsExist
        ? parsedQueryString.specialConditions
        : [],
      doctors: isDoctorsExist ? parsedQueryString.doctors : [],
      tags: isTagsExist ? parsedQueryString.tags : [],
      visitTypes: isVisitTypesExist ? parsedQueryString.visitTypes : [],
      subtenants: isSubtenantsExist ? parsedQueryString.subtenants : [],
    });
    if (!isPlacesExist) setSelectedPlacesResources([]);
    if (!isSpecialConditionsExist) setSelectedSpecialConditionsResources([]);
    if (!isDoctorsExist) setSelectedDoctors([]);
    if (!isTagsExist) setSelectedTags([]);
    if (!isVisitTypesExist) setSelectedVisitTypes([]);
    if (!isSubtenantsExist) setSelectedSubtenants([]);
  }, [
    placeResources,
    specialConditionResources,
    doctors,
    allTags,
    visitTypes,
    subtenants,
  ]);

  return (
    <div className={styles.filterContainer} data-test-id={'calendar-filter'}>
      <p className={styles.header}>
        {(i18n.t('header') as string).toUpperCase()}
      </p>
      <div className={styles.separatorLine} />
      {hasFilterBySubtenants && subtenants.length > 1 && (
        <Field
          className={styles.multiselect}
          title={i18n.t('fields.subtenants')}
        >
          <MultiSelectDropDownInput
            options={subtenants}
            idFunction={'id'}
            getOptionLabel={'name'}
            placeholder={i18n.t('control.select_subtenants')}
            allSelectedLabel={i18n.t('control.all_subtenants')}
            onValueChanged={(subtenants) => {
              selectSubtenants(subtenants);
            }}
            values={selectedSubtenants}
            headerTitle={i18n.t('fields.subtenants')}
            hasSearchFilter={true}
          />
          <div className={styles.separatorLine} />
        </Field>
      )}
      {hasFilterByVisitTypes && (
        <Field
          title={i18n.t('fields.visit_types')}
          className={styles.multiselect}
        >
          <MultiSelectDropDownInput
            options={translatedVisitTypes}
            idFunction={'id'}
            getOptionLabel={'label'}
            placeholder={i18n.t('control.select_visit_types')}
            allSelectedLabel={i18n.t('control.all_visit_types')}
            onValueChanged={(types) => {
              selectVisitTypes(types);
            }}
            values={selectedVisitTypes}
            headerTitle={i18n.t('fields.visit_types')}
            hasSearchFilter={true}
          />
          <div className={styles.separatorLine} />
        </Field>
      )}
      <div className={styles.checkboxGroupContainer}>
        {hasFilterByDoctors && (
          <Field
            title={i18n.t('fields.doctors')}
            className={styles.multiselect}
          >
            <MultiSelectDropDownInput
              options={doctors}
              idFunction={'id'}
              getOptionLabel={'name'}
              placeholder={i18n.t('control.select_doctors')}
              allSelectedLabel={i18n.t('control.all_doctors')}
              onValueChanged={(doctors) => {
                selectDoctors(doctors);
              }}
              values={selectedDoctors}
              headerTitle={i18n.t('fields.doctors')}
              hasSearchFilter={true}
            />
            <div className={styles.separatorLine} />
          </Field>
        )}
        {hasFilterByPlaces && (
          <Field
            title={i18n.t('fields.resources_places')}
            className={styles.multiselect}
          >
            <MultiSelectDropDownInput
              options={placeResources}
              idFunction={'id'}
              getOptionLabel={'name'}
              placeholder={i18n.t('control.select_places')}
              allSelectedLabel={i18n.t('control.all_places')}
              onValueChanged={(places) => {
                selectPlacesResources(places);
              }}
              values={selectedPlacesResources}
              headerTitle={i18n.t('fields.resources_places')}
              hasSearchFilter={true}
            />
            <div className={styles.separatorLine} />
          </Field>
        )}
        {hasFilterBySpecialConditions && (
          <Field title={i18n.t('fields.resources_special_conditions')}>
            <MultiSelectDropDownInput
              options={specialConditionResources}
              idFunction={'id'}
              getOptionLabel={'name'}
              placeholder={i18n.t('control.select_special_conditions')}
              allSelectedLabel={i18n.t('control.all_special_conditions')}
              onValueChanged={(resources) => {
                selectSpecialConditionsResources(resources);
              }}
              values={selectedSpecialConditionsResources}
              headerTitle={i18n.t('fields.resources_special_conditions')}
              hasSearchFilter={true}
            />
            <div className={styles.separatorLine} />
          </Field>
        )}
      </div>
      {hasFilterByTags && (
        <Field title={i18n.t('fields.tags')} className={styles.dropdown}>
          <MultiSelectDropDownInput<DictionaryRecordDto, 'id'>
            options={allTags}
            idFunction={'id'}
            getOptionLabel={'value'}
            placeholder={i18n.t('control.select_tags')}
            allSelectedLabel={i18n.t('control.all_tags')}
            onValueChanged={(newValues) => {
              selectTags(newValues);
            }}
            values={selectedTags}
            headerTitle={i18n.t('fields.tags')}
            hasSearchFilter={true}
          />
          <div className={styles.separatorLine} />
        </Field>
      )}
      {hasFilterByVisitStatuses && (
        <Field
          title={i18n.t('fields.visit_statuses')}
          className={styles.dropdown}
        >
          <MultiSelectDropDownInput
            options={translatedVisitStatuses}
            idFunction={'value'}
            getOptionLabel={'label'}
            placeholder={i18n.t('control.select_visit_statuses')}
            allSelectedLabel={i18n.t('control.all_visit_statuses')}
            onValueChanged={(newValues) => {
              setSelectedVisitStatuses(newValues.map((ev) => ev.value));
            }}
            values={selectedVisitStatuses}
            headerTitle={i18n.t('fields.visit_statuses')}
            hasSearchFilter={true}
          />
          <div className={styles.separatorLine} />
        </Field>
      )}
      <div className={styles.buttonContainer}>
        <Button
          color={ButtonColor.Primary}
          className={styles.button}
          title={i18n.t('control.reset_button')}
          onClick={() => {
            resetConfigurationValues(configuration);
            setConfigurationQueryParams(configuration);
          }}
        />
        <Button
          className={styles.button}
          title={i18n.t('control.apply_button')}
          onClick={() => {
            setQueryString({
              subtenants: selectedSubtenants,
              doctors: selectedDoctors,
              visitTypes: selectedVisitTypes,
              places: selectedPlacesResources,
              specialConditions: selectedSpecialConditionsResources,
              tags: selectedTags,
              visitStatuses: selectedVisitStatuses,
            });
          }}
        />
      </div>
    </div>
  );
};
